import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import getDirtyFieldsHelper from '@/Domain/utils/getDirtyFieldsHelper';

// 800ms idle before we autosave: long enough that a normal typing pace coalesces
// into a single PATCH (per-keystroke saves were producing 3+ in-flight Knowledge
// Base writes per identity-step session in the wild), short enough that the user
// still sees their edits persist before they reach for Continue.
const AUTO_SUBMIT_DEBOUNCE_MS = 800;

export const useAutoSubmit = <T extends FieldValues>(
  form: UseFormReturn<T>,
  onSubmitCallback: (data: Partial<T> | T) => void,
  partial = true,
  flushOnUnmount = false,
): { flush: () => void } => {
  const [isReady, setIsReady] = useState(false);
  const data = useWatch({ control: form.control });

  const onSubmit = (data: T) => {
    const dirtyData = getDirtyFieldsHelper<
      T,
      typeof form.formState.dirtyFields,
      typeof form.formState.errors
    >(data, form.formState.dirtyFields, form.formState.errors);

    if (!partial) {
      return onSubmitCallback(data as T);
    }

    if (
      (form.formState.isDirty && Object.keys(dirtyData).length !== 0) ||
      Object.keys(dirtyData).length !== 0
    ) {
      onSubmitCallback(dirtyData);
    }
  };

  const onError = (errors: FieldErrors): void => {
    const fields = Object.entries(form.formState.dirtyFields).reduce((acc, [key]) => {
      if (errors[key]) {
        return acc;
      }

      return { ...acc, [key]: form.getValues()[key] };
    }, {});

    if (Object.keys(fields).length > 0) {
      onSubmit(fields as T);
    }
  };

  const autoSubmit = useCallback(
    debounce(() => {
      form.handleSubmit(onSubmit, onError)();
    }, AUTO_SUBMIT_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!isReady) return;

    if (form.formState.isDirty || !partial) {
      autoSubmit();
    }
  }, [data, form.formState.isDirty, partial]);

  // On unmount, cancel the pending debounced save so it doesn't fire against a
  // stale form (e.g. navigating away mid-keystroke) — unless the consumer opts
  // into flushing instead: forms that unmount on in-page navigation (the
  // translations rail remounts its editor per language) would otherwise silently
  // drop the last keystrokes of an edit.
  useEffect(
    () => () => (flushOnUnmount ? autoSubmit.flush() : autoSubmit.cancel()),
    [autoSubmit, flushOnUnmount],
  );

  // Synchronous flush: if the user advances (Continue, mode-switch, blur)
  // while the debounce timer is still pending, run the save right now. Without
  // this, a fast typist hitting Continue inside the 800ms window would race
  // the in-flight debounce — sometimes the navigate would land before the
  // PATCH fired, dropping the last few characters.
  const flush = useCallback(() => autoSubmit.flush(), [autoSubmit]);

  // Stable object so consumers using the result in deps (e.g. useImperativeHandle
  // for IdentityStep) don't re-run on every render.
  return useMemo(() => ({ flush }), [flush]);
};
